import Breakpoints from './breakpoints'
import isIE, { isEdge } from './userAgent'

export default class IntersectionObserverManager {
    constructor (options, isActivateIeAndEdge = false) {
        // All items to observe, record to change the threshold if needed
        this.items = new Map()
        this.isLandscape = null
        this.breakpoints = new Breakpoints()
        this.breakpoints.on('orientation', orientation => this.setOrientation(orientation.landscape))
        this.isActivateIeAndEdge = isActivateIeAndEdge
        this.setOptions(options)
    }

    /**
     * Define option for IntersectionObserver depending to orientation
     * @param {Object} options
     * @param {Object} options.isLandscape
     * @param {number[]} options.isLandscape.above default [0]
     * @param {string} options.isLandscape.above default '20% 0px'
     * @param {Object} options.isPortrait
     * @param {number[]} options.isPortrait.above default [0]
     * @param {string} options.isPortrait.above default '20% 0px'
     */
    setOptions (options = {}) {
        if (!this.hasIntersectionObserver()) {
            return
        }
        this.settings = {
            isLandscape: {
                above: [0],
                rootMargin: '200% 0px'
            },
            isPortrait: {
                above: [0],
                rootMargin: '200% 0px'
            },
            ...options
        }
        // Rebuild observer with new values
        this.setOrientation(this.breakpoints.landscape)
    }

    /**
     * isIeOrEdge
     * Know if IE or Edge browser
     * @returns {boolean}
     */
    isIeOrEdge () {
        return isEdge() || isIE()
    }

    /**
     * IntersectionObserver and IntersectionObserverEntry
     * features are natively supported.
     * @returns {boolean}
     */
    hasIntersectionObserver () {
        // I'm not Ie/Edge or I'm IE/Edge but force to be active even with bad performances and have IntersectionObserver
        return (!this.isIeOrEdge() || (this.isIeOrEdge() && this.isActivateIeAndEdge)) &&
            ('IntersectionObserver' in window &&
                'IntersectionObserverEntry' in window)
    }

    /**
     * Initialise IntersectionObserver with threshold depending to orientation
     * @param {boolean} isLandscape is viewport in landscape mode
     */
    setOrientation (isLandscape) {
        if (!this.hasIntersectionObserver()) {
            return
        }

        // No change required
        if (this.isLandscape === isLandscape) {
            return
        }

        // Remove previous instance
        if (this.observer) {
            this.unobserveItems()
            this.observer.disconnect()
        }

        this.isLandscape = isLandscape
        const { above, rootMargin } = this.settings[isLandscape ? 'isLandscape' : 'isPortrait']

        // Create an observer
        this.observer = new IntersectionObserver((IntersectionObserverEntry) => this.onChange(IntersectionObserverEntry), {
            threshold: above,
            rootMargin: rootMargin
        })
    }

    /**
     * Add an item to observe or update the callback if already observe
     * @param {HTMLElement} data.element element to observe
     * @param {function} data.onViewportEnter
     * @param {function} data.onViewportOut
     */
    addItem (data) {
        this.hasIntersectionObserver() ? this.observeItem(data)
            : this.resolve(data)
    }

    /**
     * Observe item once
     * @param {HTMLElement} item element to observe
     * @param {function} onViewportEnter
     * @param {function} onViewportOut
     */
    observeItem (data) {
        const { element } = data
        if (!this.observer) {
            throw new Error('You must call `setOrientation` before starting to observe item')
        }

        // Item not observe
        if (!this.items.has(element)) {
            this.observer.observe(element)
        }

        delete data.element
        this.items.set(element, data)
    }

    /**
     * Set and launch directly the callback
     * @param {HTMLElement} item element to observe
     * @param {function} onViewportEnter
     */
    resolve ({ element, onViewportEnter }) {
        this.items.set(element, { onViewportEnter })
        this.callCallback({
            target: element
        })
    }

    /**
     * Check if element are in the viewport
     * @param {IntersectionObserverEntry} observable
     * @param {HTMLElement} observable.target
     * @param {Boolean} observable.isIntersecting
     */
    onChange (observables) {
        observables.forEach(observable => {
            // Old browser who does not have `isIntersecting`
            if (observable.isIntersecting === true || (typeof observable.isIntersecting === 'undefined' && observable.intersectionRatio > 0)) {
                this.callCallback(observable)

                // Unobserve is has no callback for not in viewport
                if (!this.getCallbackNotInViewPort(observable)) {
                    this.unobserve(observable)
                }
            } else {
                this.callCallbackNotInViewPort(observable)
            }
        })
    }

    /**
     * Run the callback
     * @param {IntersectionObserverEntry} observable
     * @param {HTMLElement} observable.target
     */
    callCallback (observable) {
        const cb = this.getCallback(observable)

        if (cb) {
            cb()
        }
    }

    /**
     * Run the callback when not in Viewport
     * @param {IntersectionObserverEntry} observable
     * @param {HTMLElement} observable.target
     */
    callCallbackNotInViewPort (observable) {
        const cb = this.getCallbackNotInViewPort(observable)
        if (cb) {
            cb()
        }
    }

    /**
     * Return callback for element
     * @param {HTMLElement} observable.target
     * @return {Boolean|function}
     */
    getCallback (observable) {
        const { onViewportEnter } = this.items.get(observable.target) || {}

        if (typeof onViewportEnter === 'function') {
            return onViewportEnter
        }

        return false
    }

    /**
     * Return callback for element
     * @param {HTMLElement} observable.target
     * @return {Boolean|function}
     */
    getCallbackNotInViewPort (observable) {
        const { onViewportOut } = this.items.get(observable.target) || {}

        if (typeof onViewportOut === 'function') {
            return onViewportOut
        }

        return false
    }

    /**
     * Stop watching element
     * @param {IntersectionObserverEntry} observable
     * @param {HTMLElement} observable.target
     */
    unobserve (observable) {
        if (this.observer) {
            // Stop observing the current target
            this.observer.unobserve(observable.target)
        }

        this.items.delete(observable.target)
    }

    /**
     * Unobserve all items
     */
    unobserveItems () {
        this.items.forEach((_, element) => this.observer.unobserve(element))
    }

    /**
     * Observe all items
     */
    observeItems () {
        this.items.forEach((_, element) => this.observer.observe(element))
    }
}
